// MLS submission desk helpers (pure; tested in mlsSubmission.test.js).
// Bricks never pushes to a board: Submitted / Live are the agent's own record
// after they submit in Matrix / PropTx / REALTOR.ca.

export const BOARD_LABEL = { trreb: 'TRREB', pillar9: 'Pillar 9', ddf: 'CREA DDF' };
export const BOARD_SYSTEM = { trreb: 'Matrix / PropTx', pillar9: 'Pillar 9 Matrix', ddf: 'REALTOR.ca (DDF)' };
export const STATUS_LABEL = { draft: 'Draft', ready: 'Validated', submitted: 'Submitted', live: 'Live' };
export const STATUS_TONE = { draft: 'warn', ready: 'neutral', submitted: 'brick', live: 'ok' };

export function defaultBoard(province) {
  const prov = String(province || 'ON').trim().toUpperCase();
  if (prov === 'AB') return 'pillar9';
  if (prov === 'ON') return 'trreb';
  return 'ddf';
}

const norm = (v) => (v === null || v === undefined ? '' : String(v));
export const hasValue = (v) => norm(v).trim() !== '';

// Only the fields the agent actually changed from what's on the listing.
export function changedOverrides(overrides, sections) {
  const out = {};
  const fields = (sections || []).flatMap((s) => s.fields || []);
  for (const f of fields) {
    if (!Object.prototype.hasOwnProperty.call(overrides || {}, f.key)) continue;
    const next = norm(overrides[f.key]);
    if (next.trim() !== norm(f.value).trim()) out[f.key] = next;
  }
  return out;
}

// Readiness as the agent sees it while typing (saved values + unsaved edits).
export function draftReadiness(sections, overrides) {
  let total = 0;
  let ready = 0;
  const missing = [];
  for (const s of sections || []) {
    for (const f of s.fields || []) {
      if (!f.required) continue;
      total += 1;
      const v = overrides && Object.prototype.hasOwnProperty.call(overrides, f.key) ? overrides[f.key] : f.value;
      if (hasValue(v)) ready += 1;
      else missing.push({ key: f.key, label: f.label || f.key, section: s.title || '' });
    }
  }
  return { required_total: total, required_ready: ready, missing, ready: total > 0 && ready === total };
}

export function readinessLine(board, r) {
  if (!r) return '';
  return `${BOARD_LABEL[board] || board} · ${r.required_ready} of ${r.required_total} required fields ready`;
}

// Why "Mark ready" can't be pressed yet ('' when it can).
export function markReadyBlock(r, unsavedCount) {
  if (unsavedCount > 0) return `Save ${unsavedCount} change${unsavedCount === 1 ? '' : 's'} to the listing first`;
  if (!r) return 'Loading package…';
  const n = (r.missing || []).length;
  if (n > 0) return `${n} required field${n === 1 ? '' : 's'} still empty`;
  return '';
}

export function fmtMinutes(m) {
  if (m === null || m === undefined || Number.isNaN(Number(m))) return '—';
  const total = Math.max(0, Math.round(Number(m)));
  if (total < 60) return `${total}m`;
  const h = Math.floor(total / 60);
  const mm = total % 60;
  if (h < 24) return mm ? `${h}h ${mm}m` : `${h}h`;
  const d = Math.floor(h / 24);
  const hh = h % 24;
  return hh ? `${d}d ${hh}h` : `${d}d`;
}

// KPI tiles from /api/mls/submissions/summary — real counts only, honest when empty.
export function submissionKpis(summary) {
  const k = (summary && summary.kpis) || {};
  const boards = k.live_boards || 0;
  return [
    { l: 'Submitted · 30d', v: String(k.submitted_30d || 0), s: 'last 30 days' },
    { l: 'Live listings', v: String(k.live || 0), s: boards ? `across ${boards} board${boards === 1 ? '' : 's'}` : 'none marked live yet' },
    { l: 'Validation errors', v: String(k.needs_fix || 0), s: k.needs_fix ? 'needs a fix' : 'nothing to fix', down: !!k.needs_fix },
    { l: 'Avg. time to live', v: fmtMinutes(k.avg_submit_to_live_minutes), s: k.avg_submit_to_live_minutes == null ? 'none yet' : 'submit → live' },
  ];
}

// The validation panel's listing: first queue item that needs a fix, else the first in queue.
export function focusItem(queue, selectedId) {
  const q = Array.isArray(queue) ? queue : [];
  if (selectedId) {
    const hit = q.find((x) => x.id === selectedId);
    if (hit) return hit;
  }
  return q.find((x) => x.status === 'draft' && x.missing_count > 0) || q[0] || null;
}
